import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from 'AuthContext';
// UI
import { Box, CssBaseline, Drawer, ThemeProvider, useMediaQuery } from '@mui/material';
import PullToRefreshIndicator from 'UI/PullToRefreshIndicator';
// Context
import useClubContext from 'contexts/Club';
import ClubReportingProvider from 'contexts/ClubReporting/ClubReportingProvider';
import { useKeyboardContext } from 'contexts/Keyboard';
// Components
import BottomNav from 'components/BottomNav';
import Header from 'components/Header';
import NavigationContent from 'components/NavigationContent';
// Hooks
import { usePullToRefresh } from 'hooks/usePullToRefresh';
// Utils
import { buildTheme } from 'theme';
import { resolveThemeOverrides } from 'utils/themeResolver';
import { getPlatform } from 'utils/platformHelpers';
import { syncIosKeyboardForTheme, syncIosStatusBarForTheme, syncIosWindowBackground } from 'utils/capacitorNative';

const ROUTE_TITLES = {
  '/': 'Dashboard',
  '/feed': 'Feed',
  '/books': 'Books',
  '/books/club': 'Reporting',
  '/goals': 'Goals',
  '/goals/club': 'Goals',
  '/calendar': 'Calendar',
  '/club/manage': 'Manage Club',
  '/profile': 'Profile',
  '/meetings': 'Meetings',
  '/quotes': 'Quotes',
  '/join-club': 'Join Club',
};

/** iOS-native page-fade CSS class name — keyframes defined in index.css. */
const IOS_FADE_CLASS = 'ios-page-fade';

const Layout = ({ children, onRefresh }) => {
  const { user, userProfile, logout } = useAuth();
  const { currentClub } = useClubContext();
  const { keyboardVisible } = useKeyboardContext();
  const location = useLocation();
  
  // Get user's theme override for current club or global settings
  const userThemeOverrides = userProfile?.settings?.clubThemeOverrides || {};
  const userClubThemeOverride =
    userThemeOverrides[String(currentClub?.id)] || userThemeOverrides.all;
  
  // Build theme with resolved overrides
  const clubTheme = useMemo(() => {
    const baseOverrides = currentClub?.themeOverrides || {};
    const effectiveOverrides = resolveThemeOverrides(baseOverrides, userClubThemeOverride);
    return buildTheme(effectiveOverrides);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentClub?.id, currentClub?.themeOverrides, userClubThemeOverride]);
  
  const isMobile = useMediaQuery(clubTheme.breakpoints.down('md'));
  const isIosNative = getPlatform() === 'ios';

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  const headerRef = useRef(null);
  const mainRef = useRef(null);
  const [headerHeight, setHeaderHeight] = useState(56); // Default to 56px (MUI Toolbar default)

  const handleRefresh = useCallback(() => (onRefresh ? onRefresh() : Promise.resolve()), [onRefresh]);
  const pullToRefresh = usePullToRefresh({
    ref: mainRef,
    onRefresh: handleRefresh,
    direction: 'top',
    disabled: !onRefresh,
  });

  // Measure header height for proper spacing (only relevant on desktop now)
  useEffect(() => {
    if (isMobile) {
        setHeaderHeight(0);
        return;
    }

    const measureHeader = () => {
      // Since AppBar is fixed, we need to find it in the document
      const appBar = document.querySelector('.MuiAppBar-root');
      if (appBar) {
        const height = appBar.offsetHeight;
        setHeaderHeight(height);
      } else {
        // Fallback: measure wrapper if AppBar not found yet
        if (headerRef.current) {
          const toolbar = headerRef.current.querySelector('.MuiToolbar-root');
          if (toolbar) {
            const height = toolbar.offsetHeight;
            if (height > 0) {
              setHeaderHeight(height);
            }
          }
        }
      }
    };
    
    // Measure immediately
    measureHeader();
    
    // Also measure after a delay to catch slower renders
    const timeout = setTimeout(measureHeader, 100);
    const timeout2 = setTimeout(measureHeader, 300);
    
    // Use ResizeObserver if available
    let resizeObserver;
    const appBar = document.querySelector('.MuiAppBar-root');
    if (appBar && window.ResizeObserver) {
      resizeObserver = new ResizeObserver(measureHeader);
      resizeObserver.observe(appBar);
    }
    
    return () => {
      clearTimeout(timeout);
      clearTimeout(timeout2);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [currentClub, isMobile]); // Re-measure when club changes (affects header height) or mobile state changes

  const pageTitle = ROUTE_TITLES[location.pathname] || 'Dashboard';

  useEffect(() => {
    syncIosStatusBarForTheme(clubTheme.palette.mode);
    syncIosKeyboardForTheme(clubTheme.palette.mode);
    syncIosWindowBackground(clubTheme.palette.background.default);
  }, [clubTheme.palette.mode, clubTheme.palette.background.default]);

  // Update page title based on route and club
  useEffect(() => {
    const clubName = currentClub?.name || '';
    const title = clubName ? `${clubName} - ${pageTitle}` : pageTitle;

    document.title = title;
  }, [location.pathname, currentClub, pageTitle]);

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      // Error logging out
    }
  };

  return (
    <ThemeProvider theme={clubTheme}>
      <ClubReportingProvider>
        <CssBaseline />
        <Box
          sx={{
            height: '100%',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Desktop Header - Hidden on Mobile */}
          <Box 
              ref={headerRef} 
              sx={{ display: { xs: 'none', md: 'block' } }}
          >
            <Header 
              user={user} 
              onMenuClick={() => setDrawerOpen(true)} 
              onLogout={handleLogout} 
            />
          </Box>
          
          {/* Desktop Drawer (Left) */}
          <Drawer 
              anchor="left" 
              open={drawerOpen} 
              onClose={() => setDrawerOpen(false)}
          >
              <NavigationContent 
                  onClose={() => setDrawerOpen(false)}
                  onLogout={handleLogout}
                  isMobile={false}
              />
          </Drawer>

          {/* Mobile Menu Drawer (Right) */}
          <Drawer
              anchor="right"
              open={mobileMenuOpen}
              onClose={() => setMobileMenuOpen(false)}
              PaperProps={{
                  sx: {
                    width: '85%',
                    maxWidth: 300,
                    paddingTop: 'env(safe-area-inset-top)',
                    paddingBottom: 'env(safe-area-inset-bottom)',
                  },
              }}
          >
              <NavigationContent
                  onClose={() => setMobileMenuOpen(false)}
                  onLogout={handleLogout}
                  isMobile={true}
                  menuOpen={mobileMenuOpen}
              />
          </Drawer>

          {/* Spacer for fixed header (Desktop only) */}
          <Box sx={{ height: `${headerHeight}px`, flexShrink: 0, display: { xs: 'none', md: 'block' } }} />

          <Box
            ref={mainRef}
            component="main"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              flex: 1,
              overflow: 'scroll',
              minHeight: 0,
              // Mobile: space for fixed BottomNav; drop when keyboard is open (native) so content meets keyboard
              paddingBottom: {
                xs: keyboardVisible ? 0 : 'calc(56px + env(safe-area-inset-bottom))',
                md: 0,
              },
            }}
          >
            {onRefresh && (
              <PullToRefreshIndicator
                direction="top"
                pullProgress={pullToRefresh.pullProgress}
                pullDistance={pullToRefresh.pullDistance}
                isRefreshing={pullToRefresh.isRefreshing}
              />
            )}
            <Box
              key={location.pathname}
              className={isMobile && isIosNative ? IOS_FADE_CLASS : undefined}
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignSelf: 'stretch',
                minWidth: 0,
              }}
            >
              {children}
            </Box>
          </Box>

          {/* Mobile Bottom Nav - Hidden on Desktop */}
          <Box sx={{ display: { xs: 'block', md: 'none' } }}>
              <BottomNav onMenuClick={() => setMobileMenuOpen(true)} />
          </Box>
        </Box>
      </ClubReportingProvider>
    </ThemeProvider>
  );
};

export default Layout;
